/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  items,
  shape,
  fromModel,
  email,
  required,
  stopOnFirstFailure,
  when,
  Validator,
  Failure,
  Success,
  ValidationError
} from "@totalsoft/pure-validations";
import { isPhoneByCountry } from "utils/pure-validation/primitiveValidators";
import { areMultipleEmailsValid } from "@totalsoft/validations";
import { useDirtyFieldValidation } from "@totalsoft/pure-validations-react";
import { useDirtyInfo } from "@totalsoft/change-tracking-react";
import { useTranslation } from "react-i18next";
import ContactInfoComponent from "./ContactInfoComponent";
import {
  loadContactModes,
  loadPositions,
  loadPartnerContactMode,
  loadContactInfos,
  saveContactModes,
  saveContactInfos,
  loadPartnerDetails,
  loadPartnerContactInfoSummary,
  loadSegregatedPersons
} from "../../actionCreators";
import {
  getContactModes,
  getPositions,
  getPartnerContactModes,
  getContactInfos,
  getPartnerContactInfoSummary
} from "../../selectors";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withParams } from "../../../../providers/ParamsConsumer";

export const contactModesValidator = (contactModeParams, defaultCountryCode) => {
  return items(
    fromModel(item =>
      shape({
        contactModeId: required,
        contactInfo:
          [
            required,
            email |> when(item.contactModeId === contactModeParams.contactModeEmail),
            isPhoneByCountry(defaultCountryCode)
              |> when(
                item.contactModeId === contactModeParams.contactModeMobilePhone ||
                  item.contactModeId === contactModeParams.contactModePhone
              )
          ] |> stopOnFirstFailure
      })
    )
  );
};

export const contactInfosValidator = (defaultCountryCode, t) => {
  const multipleEmails = Validator(function email(content) {
    if (!content) return Success;
    const emails = content.split(";");
    return areMultipleEmailsValid(emails) ? Success : Failure(ValidationError(t("Validations.Generic.MultipleEmails")));
  });
  return items(
    fromModel(item =>
      shape({
        personName: required,
        email: multipleEmails |> when(Boolean(item.email)),
        phone: isPhoneByCountry(defaultCountryCode),
        positionId: required
      })
    )
  );
};

export const ContactInfoContainer = ({
  contactModes,
  positions,
  actions,
  partnerContactModes,
  partnerContactModesLoaded,
  partnerId,
  contactInfos,
  contactInfosLoaded,
  contactModeParams,
  summaryLoaded,
  partnerContactInfoSummary,
  setIsDirty,
  hasWritePermission,
  params
}) => {
  const { t } = useTranslation();

  const [localContactInfos, setLocalContactInfoList] = useState(contactInfos);
  const [localContactModes, setLocalContactModeList] = useState(partnerContactModes);

  const [contactModeCardExpanded, setContactModeCardExpanded] = useState(false);
  const [contactInfoCardExpanded, setContactModeInfoExpanded] = useState(false);

  const validatorContactModes = useMemo(
    () => contactModesValidator(contactModeParams, params.defaultCountryCode),
    [
      contactModeParams.contactModeEmail,
      contactModeParams.contactModePhone,
      contactModeParams.contactModeMobilePhone,
      params.defaultCountryCode
    ]
  );

  const [dirtyInfo, setDirtyInfo] = useDirtyInfo();
  const [contactModesValidation, validateContactModes] = useDirtyFieldValidation(validatorContactModes);

  const validatorContactInfos = useMemo(
    () => contactInfosValidator(params.defaultCountryCode, t),
    [params.defaultCountryCode]
  );

  const [contactInfosValidation, validateContactInfos] = useDirtyFieldValidation(validatorContactInfos);

  const [saveContactModeDisabled, setSaveContactModeDisabled] = useState(true);
  const [saveContactInfoDisabled, setSaveContactInfoDisabled] = useState(true);

  const [validDefaultEmail, setValidDefaultEmail] = useState(true);
  const [validDefaultMobilePhone, setValidDefaultMobilePhone] = useState(true);
  const [validDefaultPhone, setValidDefaultPhone] = useState(true);
  const [validDefaultContactInfo, setValidDefaultContactInfo] = useState(true);
  const [validESignatureContactInfo, setValidESignatureContactInfo] = useState(true);
  const adminPositionId = useMemo(() => {
    return positions.find(position => position.positionCode === params.administratorPartnerPositionCode)?.positionId;
  }, [params.administratorPartnerPositionCode, positions]);

  const loadPartnerDetails = (uniqueID, idx) => {
    if (!uniqueID) {
      return Promise.resolve({ options: [] });
    }
    actions.loadPartnerDetails(uniqueID).then(data => {
      let contactInfoList = Object.assign([], {
        ...localContactInfos,
        [idx]: {
          ...localContactInfos[idx],
          personId: data.partnerId,
          uniqueID: data.uniqueID,
          personName: data.partnerName,
          email: data.e_Mail,
          phone: data.phoneNumber,
          phoneCountryCode: data.phoneCountryCode
        }
      });
      setLocalContactInfoList(contactInfoList);
    });
  };

  const validateUniqueDefaultContactInfo = localContactInfos => {
    return localContactInfos.filter(contactInfo => contactInfo.isDefaultContact === true);
  };

  const validateUniqueESignatureContactInfo = localContactInfos => {
    return localContactInfos.filter(contactInfo => contactInfo.isESignature === true);
  };

  const validateUniqueDefaultContactModes_Email = localContactModes => {
    return localContactModes.filter(
      contactMode => contactMode.isDefault === true && contactMode.contactModeId === contactModeParams.contactModeEmail
    );
  };

  const validateUniqueDefaultContactModes_MobilePhone = localContactModes => {
    return localContactModes.filter(
      contactMode =>
        contactMode.isDefault === true && contactMode.contactModeId === contactModeParams.contactModeMobilePhone
    );
  };
  const validateUniqueDefaultContactModes_Phone = localContactModes => {
    return localContactModes.filter(
      contactMode => contactMode.isDefault === true && contactMode.contactModeId === contactModeParams.contactModePhone
    );
  };

  const saveContactModes = (partnerId, localContactModes) => {
    if (!hasWritePermission || !validateContactModes(localContactModes)) {
      return;
    }
    const counterDefaultEmail = validateUniqueDefaultContactModes_Email(localContactModes);
    const counterDefaultMobilePhone = validateUniqueDefaultContactModes_MobilePhone(localContactModes);
    const counterDefaultPhone = validateUniqueDefaultContactModes_Phone(localContactModes);
    if (counterDefaultEmail.length > 1) {
      setValidDefaultEmail(false);
    } else if (counterDefaultMobilePhone.length > 1) {
      setValidDefaultMobilePhone(false);
    } else if (counterDefaultPhone.length > 1) {
      setValidDefaultPhone(false);
    } else {
      actions.saveContactModes(partnerId, localContactModes, () => {
        setIsDirty(false);
      });
      setSaveContactModeDisabled(false);
    }
  };

  const saveContactInfos = (partnerId, localContactInfos) => {
    if (!hasWritePermission || !validateContactInfos(localContactInfos)) {
      return;
    }
    const counterDefaultContactInfo = validateUniqueDefaultContactInfo(localContactInfos);
    if (counterDefaultContactInfo.length > 1) {
      setValidDefaultContactInfo(false);
      return;
    }
    const counterESignatureContactInfo = validateUniqueESignatureContactInfo(localContactInfos);
    if (counterESignatureContactInfo.length > 1) {
      setValidESignatureContactInfo(false);
      return;
    }
    actions.saveContactInfos(partnerId, localContactInfos, () => {
      setIsDirty(false);
    });
    setSaveContactInfoDisabled(true);
  };

  const addContactMode = () => {
    let contactModeList = [...localContactModes, { phoneCountryCode: params.defaultCountryCode }];
    setLocalContactModeList(contactModeList);

    setSaveContactModeDisabled(false);
    setIsDirty(true);
    setValidDefaultEmail(true);
    setValidDefaultMobilePhone(true);
    setValidDefaultPhone(true);
  };

  const addContactInfo = () => {
    let contactInfoList = [...localContactInfos, {}];
    setLocalContactInfoList(contactInfoList);
    setSaveContactInfoDisabled(false);
    setIsDirty(true);
    setValidDefaultContactInfo(true);
    setValidESignatureContactInfo(true);
  };

  const deleteContactMode = item => {
    setDirtyInfo(`${localContactModes.indexOf(item)}`);
    setLocalContactModeList(localContactModes.filter(x => x !== item));
    setSaveContactModeDisabled(false);
    setIsDirty(true);
  };

  const deleteContactInfo = item => {
    setDirtyInfo(`${localContactInfos.indexOf(item)}`);
    let newContactInfoList = localContactInfos.filter(
      l => localContactInfos.indexOf(l) !== localContactInfos.indexOf(item)
    );
    setLocalContactInfoList(newContactInfoList);
    setSaveContactInfoDisabled(false);
  };

  const onContactModeCardExpand = expanded => {
    if (contactModeCardExpanded !== expanded) setContactModeCardExpanded(expanded);
  };

  const onContactInfoCardExpand = expanded => {
    if (contactInfoCardExpanded !== expanded) setContactModeInfoExpanded(expanded);
  };

  useEffect(() => {
    actions.loadPartnerContactInfoSummary(partnerId);
  }, []);

  useEffect(() => {
    if (!summaryLoaded) {
      actions.loadPartnerContactInfoSummary(partnerId);
    }
  }, [summaryLoaded]);

  useEffect(() => {
    if (contactModeCardExpanded && !partnerContactModesLoaded) {
      actions.loadContactModes();
      if (partnerId !== null) actions.loadPartnerContactMode(partnerId);
    }
  }, [contactModeCardExpanded]);

  useEffect(() => {
    if (contactInfoCardExpanded && !contactInfosLoaded) {
      actions.loadPositions();
      if (partnerId !== null) actions.loadContactInfos(partnerId);
    }
  }, [contactInfoCardExpanded]);

  useEffect(() => {
    if (contactInfosLoaded) {
      setLocalContactInfoList(contactInfos);
    }
  }, [contactInfosLoaded]);

  useEffect(() => {
    if (partnerContactModesLoaded) {
      setLocalContactModeList(partnerContactModes);
    }
  }, [partnerContactModesLoaded]);

  const loadPersons = search => {
    if (!search) {
      return Promise.resolve([]);
    }

    return actions.loadSegregatedPersons(search).then(data => data.map(({ id, uniqueID }) => ({ id, uniqueID })));
  };

  const onContactInfoPropertyChange =
    idx =>
    (prop1, prop2, callbackFn = () => {}) =>
    (value1, value2) => {
      let contactInfoList = Object.assign([], {
        ...localContactInfos,
        [idx]: {
          ...localContactInfos[idx],
          [prop1]: value1,
          ...(prop2 && { [prop2]: value2 })
        }
      });

      if (value2) {
        callbackFn(value2, idx);
      }
      if (prop1 === "positionId" && value1 === adminPositionId) {
        contactInfoList = Object.assign([], {
          ...contactInfoList,
          [idx]: {
            ...contactInfoList[idx],
            isLegalRepresentative: true
          }
        });
      }
      setLocalContactInfoList(contactInfoList);
      setSaveContactInfoDisabled(false);
      setValidDefaultContactInfo(true);
      setValidESignatureContactInfo(true);
      setIsDirty(true);
    };

  const onContactModePropertyChange = idx => prop => value => {
    setDirtyInfo(`${idx}.${prop}`);
    setLocalContactModeList(
      Object.assign([], {
        ...localContactModes,
        [idx]: {
          ...localContactModes[idx],
          [prop]: value
        }
      })
    );
    setSaveContactModeDisabled(false);
    setValidDefaultEmail(true);
    setValidDefaultMobilePhone(true);
    setValidDefaultPhone(true);
    setIsDirty(true);
  };

  useEffect(() => {
    validateContactModes(localContactModes, dirtyInfo);
  }, [localContactModes, dirtyInfo, validateContactModes]);

  useEffect(() => {
    validateContactInfos(localContactInfos, dirtyInfo);
  }, [localContactInfos, dirtyInfo, validateContactInfos]);

  return (
    <ContactInfoComponent
      contactModeList={localContactModes}
      contactModesValidation={contactModesValidation}
      contactInfosValidation={contactInfosValidation}
      contactInfoList={localContactInfos}
      onContactInfoPropertyChange={onContactInfoPropertyChange}
      onContactModePropertyChange={onContactModePropertyChange}
      contactModes={contactModes}
      positions={positions}
      loadPersons={loadPersons}
      deleteContactMode={deleteContactMode}
      saveContactModes={saveContactModes}
      addContactMode={addContactMode}
      deleteContactInfo={deleteContactInfo}
      saveContactInfos={saveContactInfos}
      addContactInfo={addContactInfo}
      partnerId={partnerId}
      contactModeCardExpanded={contactModeCardExpanded}
      onContactModeCardExpand={onContactModeCardExpand}
      contactInfoCardExpanded={contactInfoCardExpanded}
      onContactInfoCardExpand={onContactInfoCardExpand}
      saveContactModeDisabled={saveContactModeDisabled}
      setSaveContactModeDisabled={setSaveContactModeDisabled}
      saveContactInfoDisabled={saveContactInfoDisabled}
      validDefaultContactInfo={validDefaultContactInfo}
      validESignatureContactInfo={validESignatureContactInfo}
      validDefaultEmail={validDefaultEmail}
      validDefaultMobilePhone={validDefaultMobilePhone}
      validDefaultPhone={validDefaultPhone}
      loadPartnerDetails={loadPartnerDetails}
      partnerContactInfoSummary={partnerContactInfoSummary}
      hasWritePermission={hasWritePermission}
    />
  );
};
ContactInfoContainer.propTypes = {
  contactModes: PropTypes.array.isRequired,
  positions: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  partnerId: PropTypes.number,
  partnerContactModes: PropTypes.array,
  partnerContactModesLoaded: PropTypes.bool.isRequired,
  partnerContactModesLoading: PropTypes.bool.isRequired,
  contactInfos: PropTypes.array,
  contactInfosLoaded: PropTypes.bool.isRequired,
  contactInfosLoading: PropTypes.bool.isRequired,
  contactModeParams: PropTypes.object.isRequired,
  summaryLoaded: PropTypes.bool,
  partnerContactInfoSummary: PropTypes.object,
  setIsDirty: PropTypes.func,
  hasWritePermission: PropTypes.bool.isRequired,
  params: PropTypes.object
};

function mapStateToProps(state, ownProps) {
  const contactModes = getContactModes(state);
  const positions = getPositions(state);

  const { partnerContactInfoSummary, summaryLoaded } = getPartnerContactInfoSummary(state, ownProps.partnerId);

  const {
    content: contactInfos,
    loaded: contactInfosLoaded,
    loading: contactInfosLoading
  } = getContactInfos(state, ownProps.partnerId);

  const {
    content: partnerContactModes,
    loaded: partnerContactModesLoaded,
    loading: partnerContactModesLoading
  } = getPartnerContactModes(state, ownProps.partnerId);

  return {
    contactModes,
    positions,
    partnerContactModes,
    partnerContactModesLoaded,
    partnerContactModesLoading,
    contactInfos,
    contactInfosLoaded,
    contactInfosLoading,
    summaryLoaded,
    partnerContactInfoSummary
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadContactModes,
        loadSegregatedPersons,
        loadPositions,
        loadPartnerContactMode,
        loadContactInfos,
        saveContactModes,
        saveContactInfos,
        loadPartnerDetails,
        loadPartnerContactInfoSummary
      },
      dispatch
    ),
    dispatch
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withParams(ContactInfoContainer));
